/**
 * Pricing Section
 * Home pricing and loan calculation - Figma design implementation
 */

import * as React from "react";
import {
  Add01Icon,
  AiMagicOldIcon,
  CashbackIcon,
  DollarSquareIcon,
  InformationCircleIcon,
  MinusSignIcon,
  MoneyReceive01Icon,
  TradeUpIcon,
} from "@porchpass/ui";
import {
  CircleShape,
  Flower1Shape,
  Flower2Shape,
  MoonShape,
  StarShape,
} from "@porchpass/ui";
import { AnimatedNumberInput, Input } from "@porchpass/ui";
import { Button } from "@porchpass/ui";
import { Chip } from "@porchpass/ui";
import { Spinner } from "@porchpass/ui";
import { useApplicationFormStore } from "../hooks/useApplicationFormStore";
import { useDgaApi } from "../hooks/useDgaApi";
import { DOWN_PAYMENT_SOURCE_OPTIONS } from "../../../constants/enumOptions";
import { Select, SelectItem } from "@porchpass/ui";
import type { WithEmpty } from "../types/applicationTypes";
import { DomainDownPaymentSourceType } from "@porchpass/api-client";
import {
  PRICING_DOWN_PAYMENT_SOURCE_OTHER,
  shouldClearOtherField,
} from "@porchpass/ui";

const DOWN_PAYMENT_OPTIONS = [
  { value: "0", label: "0%" },
  { value: "5", label: "5%" },
  { value: "10", label: "10%" },
  { value: "15", label: "15%" },
  { value: "20", label: "20%" },
  { value: "35", label: "35%" },
];

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatCurrencyNoDecimals = (value: number): string => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Icon wrapper with decorative shape background
interface IconWrapperProps {
  shape: React.ReactNode;
  children: React.ReactNode;
}

const IconWrapper: React.FC<IconWrapperProps> = ({ shape, children }) => (
  <div className="relative w-8 h-8">
    <div className="absolute inset-0">{shape}</div>
    <div className="flex absolute inset-0 justify-center items-center">
      {children}
    </div>
  </div>
);

// Plus/Minus badge components
const PlusBadge = () => (
  <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 z-10 bg-blue-50 border-2 border-white rounded-xl px-3 py-1.5 flex items-center">
    <Add01Icon size={10} className="text-blue-500" variant="stroke-rounded" />
  </div>
);

const MinusBadge = () => (
  <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 z-10 bg-blue-50 border-2 border-white rounded-xl px-3 py-1.5 flex items-center">
    <MinusSignIcon
      size={10}
      className="text-blue-500"
      variant="stroke-rounded"
    />
  </div>
);

interface PricingRowProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  onChange?: (value: number) => void;
  subtext?: string;
}

const PricingRow: React.FC<PricingRowProps> = ({
  icon,
  label,
  value,
  onChange,
  subtext,
}) => {
  const [focused, setFocused] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleChange = (newValue: number) => {
    onChange?.(newValue);
  };

  const showActiveBorder = focused;

  return (
    <div
      className={`flex h-[72px] rounded-2xl w-full cursor-text transition-all items-center px-6 gap-4 ${
        showActiveBorder
          ? "bg-white ring-4 ring-primary"
          : "bg-grey-200 hover:bg-grey-300"
      }`}
      onClick={() => inputRef.current?.focus()}
    >
      <div className="flex gap-4 items-center shrink-0">
        {icon}
        <span className="text-[17px] font-medium text-on-surface-primary">
          {label}
        </span>
      </div>
      <div className="flex flex-col flex-1 gap-1 items-end min-w-0">
        <AnimatedNumberInput
          ref={inputRef}
          value={value}
          onChange={handleChange}
          prefix="$"
          decimals={2}
          className="w-full text-right"
          inputClassName="text-right"
          textClassName="text-xl font-semibold justify-end"
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
        {subtext && (
          <span className="font-normal text-3xs text-text-med">{subtext}</span>
        )}
      </div>
    </div>
  );
};

export const PricingCalculatorSection: React.FC = () => {
  const store = useApplicationFormStore();
  const api = useDgaApi();
  const pricing = store.data.pricing;
  const currentStep = store.currentStep;
  const homePriceError = store.fieldErrors["pricing.homePrice"];
  const downPaymentError = store.fieldErrors["pricing.downPaymentAmount"];
  const downPaymentSourceError = store.fieldErrors["pricing.downPaymentSource"];
  const downPaymentSourceOtherError =
    store.fieldErrors["pricing.downPaymentSourceOther"];
  const loanAmountLoading = store.loanAmountLoading;

  const homePrice = pricing.homePrice || 0;
  const upgrades = pricing.upgradesCost || 0;
  const siteImprovements = pricing.siteImprovementCost || 0;
  const downPaymentAmount = pricing.downPaymentAmount || 0;
  const landPurchaseOrPayoffAmount = pricing.landPurchaseOrPayoffAmount || 0;

  const yearlyPremium = pricing.yearlyPremium || 0;
  const [downPaymentPercent, setDownPaymentPercent] = React.useState("");
  const downPaymentSource = pricing.downPaymentSource || "";
  const downPaymentSourceOther = pricing.downPaymentSourceOther || "";

  // Update pricing and immediately show the loan amount spinner
  const updatePricingWithLoading = React.useCallback(
    (updates: Parameters<typeof store.updatePricing>[0]) => {
      store.setLoanAmountLoading(true);
      store.updatePricing(updates);
    },
    [store],
  );

  // Refs to avoid re-triggering the effect on every store/api reference change
  const storeRef = React.useRef(store);
  storeRef.current = store;
  const apiRef = React.useRef(api);
  apiRef.current = api;

  // Fetch fresh summary data when navigating to the Pricing page
  React.useEffect(() => {
    if (currentStep !== "pricing") return;
    const appId = storeRef.current.data.applicationId;
    if (!appId) return;

    let cancelled = false;
    apiRef.current
      .getApplicationSummary(appId)
      .then((resp) => {
        if (cancelled || resp.status !== 200) return;
        const data = resp.data;
        storeRef.current.populateFromApi({
          pricing: {
            homePrice: data.home_price ?? 0,
            upgradesCost: data.home_upgrades_cost ?? 0,
            siteImprovementCost: data.site_improvement_amount ?? 0,
            yearlyPremium: data.home_insurance_yearly_payment ?? 0,
            downPaymentAmount: data.down_payment_amount ?? 0,
            loanAmount: data.loan_total_amount ?? 0,
            ...(data.land_purchase_or_payoff_amount != null && {
              landPurchaseOrPayoffAmount: data.land_purchase_or_payoff_amount,
            }),
          },
        });
      })
      .catch((e) => {
        if (!cancelled) console.error("[Pricing] summary fetch failed:", e);
      });

    return () => {
      cancelled = true;
    };
  }, [currentStep]);

  // Calculate package price
  const packagePrice =
    homePrice + upgrades + siteImprovements + landPurchaseOrPayoffAmount;

  // Use backend-provided loan amount if available, otherwise fall back to client calculation
  const loanAmount =
    pricing.loanAmount > 0
      ? pricing.loanAmount
      : Math.max(0, packagePrice - downPaymentAmount);

  // Calculate down payment percentage
  const downPaymentPercentage =
    packagePrice > 0
      ? ((downPaymentAmount / packagePrice) * 100).toFixed(2)
      : "0";

  const clearDownPaymentSourceFieldErrors = React.useCallback(() => {
    if (store.fieldErrors["pricing.downPaymentSource"]) {
      store.clearFieldError("pricing.downPaymentSource");
    }
    if (store.fieldErrors["pricing.downPaymentSourceOther"]) {
      store.clearFieldError("pricing.downPaymentSourceOther");
    }
  }, [store]);

  const applyDownPaymentUpdate = React.useCallback(
    (amount: number) => {
      if (store.fieldErrors["pricing.downPaymentAmount"]) {
        store.clearFieldError("pricing.downPaymentAmount");
      }

      const updates: Parameters<typeof store.updatePricing>[0] = {
        downPaymentAmount: amount,
      };

      if (amount === 0) {
        updates.downPaymentSource = "";
        updates.downPaymentSourceOther = "";
        clearDownPaymentSourceFieldErrors();
      }

      updatePricingWithLoading(updates);
      setDownPaymentPercent("");
    },
    [clearDownPaymentSourceFieldErrors, store, updatePricingWithLoading],
  );

  // Handle down payment percentage chip click
  const handleDownPaymentPercentChange = (value: string) => {
    setDownPaymentPercent(value);
    const percent = parseInt(value);
    const amount = Math.round((packagePrice * percent) / 100);
    applyDownPaymentUpdate(amount);
  };

  return (
    <div className="flex flex-col gap-6 w-full max-w-[628px] px-2">
      {/* Section Header */}
      <div className="flex justify-between items-center">
        <span className="font-medium text-md text-on-surface-primary">
          Pricing
        </span>
        <Button variant="ghost" size="icon">
          <InformationCircleIcon
            variant="bulk-rounded"
            size={16}
            className="text-primary"
          />
        </Button>
      </div>

      {/* Costs section */}
      <div className="relative flex flex-col gap-1.5">
        {/* Home Price */}
        <PricingRow
          icon={
            <IconWrapper shape={<Flower1Shape size={32} fill="white" />}>
              <DollarSquareIcon
                size={18}
                className="text-[#6F41E6]"
                variant="bulk-rounded"
              />
            </IconWrapper>
          }
          label="Home Price"
          value={homePrice}
          onChange={(val) => {
            if (store.fieldErrors["pricing.homePrice"]) {
              store.clearFieldError("pricing.homePrice");
            }
            updatePricingWithLoading({ homePrice: val });
          }}
        />
        {homePriceError && (
          <p className="text-sm text-[#872525] mt-1">{homePriceError}</p>
        )}

        {/* Plus badge between Home Price and Upgrades */}
        <div className="relative h-0">
          <PlusBadge />
        </div>

        {/* Upgrades */}
        <PricingRow
          icon={
            <IconWrapper
              shape={<MoonShape size={32} fill="white" className="rotate-90" />}
            >
              <TradeUpIcon
                size={18}
                className="text-[#017C3F]"
                variant="bulk-rounded"
              />
            </IconWrapper>
          }
          label="Upgrades"
          value={upgrades}
          onChange={(val) => updatePricingWithLoading({ upgradesCost: val })}
        />

        {/* Plus badge between Upgrades and Site Improvements */}
        <div className="relative h-0">
          <PlusBadge />
        </div>

        {/* Site Improvements */}
        <PricingRow
          icon={
            <IconWrapper shape={<StarShape size={32} fill="white" />}>
              <AiMagicOldIcon
                size={18}
                className="text-[#BC3C7C]"
                variant="bulk-rounded"
              />
            </IconWrapper>
          }
          label="Site Improvements"
          value={siteImprovements}
          onChange={(val) =>
            updatePricingWithLoading({ siteImprovementCost: val })
          }
        />

        {/* Purchase Price Payoff Amount - only shown when value exists */}
        {landPurchaseOrPayoffAmount > 0 && (
          <>
            <div className="relative h-0">
              <PlusBadge />
            </div>
            <div className="flex items-center justify-between h-8 px-6 bg-[#e8f5e9] rounded-xl">
              <span className="text-xs font-medium text-on-surface-primary">
                Land Purchase Price/Payoff
              </span>
              <div className="flex items-center text-xs font-semibold text-on-surface-primary">
                <span>$</span>
                <span>{formatCurrency(landPurchaseOrPayoffAmount)}</span>
              </div>
            </div>
          </>
        )}

        {/* Package Price */}
        <div className="flex items-center justify-between h-8 px-6 bg-[#f1edfb] rounded-xl">
          <span className="text-xs font-medium text-on-surface-primary">
            Package Price
          </span>
          <div className="flex items-center text-xs font-semibold text-on-surface-primary">
            <span>$</span>
            <span>{formatCurrency(packagePrice)}</span>
          </div>
        </div>

        {/* Yearly Premium */}
        <PricingRow
          icon={
            <IconWrapper shape={<Flower2Shape size={32} fill="white" />}>
              <CashbackIcon
                size={18}
                className="text-[#8C6727]"
                variant="bulk-rounded"
              />
            </IconWrapper>
          }
          label="Yearly Insurance Premium"
          value={yearlyPremium}
          onChange={(val) => updatePricingWithLoading({ yearlyPremium: val })}
          subtext={`est. $${formatCurrencyNoDecimals(yearlyPremium / 12)}/mo`}
        />

        {/* Minus badge between Yearly Premium and Down Payment */}
        <div className="relative h-0">
          <MinusBadge />
        </div>

        {/* Down Payment with chips */}
        <div className="flex flex-col gap-2 pb-4">
          <PricingRow
            icon={
              <IconWrapper shape={<CircleShape size={32} fill="white" />}>
                <MoneyReceive01Icon
                  size={18}
                  className="text-blue-500"
                  variant="bulk-rounded"
                />
              </IconWrapper>
            }
            label="Down Payment"
            value={downPaymentAmount}
            onChange={(val) => {
              applyDownPaymentUpdate(val);
            }}
            subtext={`${downPaymentPercentage}%`}
          />
          {downPaymentError && (
            <p className="text-sm text-[#872525] mt-1">{downPaymentError}</p>
          )}

          {/* Percentage chips */}
          <div className="flex gap-2">
            {DOWN_PAYMENT_OPTIONS.map((option) => (
              <Chip
                key={option.value}
                label={option.label}
                variant="primary"
                size="sm"
                selected={downPaymentPercent === option.value}
                onSelectedChange={() =>
                  handleDownPaymentPercentChange(option.value)
                }
              />
            ))}
          </div>

          {/* Down Payment Source - shown only when downPaymentAmount > 0 */}
          {downPaymentAmount > 0 && (
            <div className="flex flex-col gap-4 mt-2">
              <Select
                value={downPaymentSource}
                onValueChange={(val: string) => {
                  if (store.fieldErrors["pricing.downPaymentSource"]) {
                    store.clearFieldError("pricing.downPaymentSource");
                  }
                  const updates: Parameters<typeof store.updatePricing>[0] = {
                    downPaymentSource:
                      val as WithEmpty<DomainDownPaymentSourceType>,
                  };
                  if (
                    shouldClearOtherField(
                      val,
                      PRICING_DOWN_PAYMENT_SOURCE_OTHER,
                    )
                  ) {
                    updates.downPaymentSourceOther = "";
                    if (store.fieldErrors["pricing.downPaymentSourceOther"]) {
                      store.clearFieldError("pricing.downPaymentSourceOther");
                    }
                  }
                  updatePricingWithLoading(updates);
                }}
                label="Down Payment Source"
                className="w-full"
                error={!!downPaymentSourceError}
                helperText={downPaymentSourceError}
              >
                {DOWN_PAYMENT_SOURCE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </Select>

              {/* Down Payment Source Other - shown when "Other" or "Gift" is selected */}
              {(downPaymentSource ===
                DomainDownPaymentSourceType.DownPaymentSourceOther ||
                downPaymentSource ===
                  DomainDownPaymentSourceType.DownPaymentSourceGift) && (
                <div className="flex flex-col gap-2">
                  <Input
                    value={downPaymentSourceOther}
                    onChange={(e) => {
                      if (store.fieldErrors["pricing.downPaymentSourceOther"]) {
                        store.clearFieldError("pricing.downPaymentSourceOther");
                      }
                      updatePricingWithLoading({
                        downPaymentSourceOther: e.target.value,
                      });
                    }}
                    label={
                      downPaymentSource ===
                      DomainDownPaymentSourceType.DownPaymentSourceOther
                        ? "Other Source"
                        : "Gift From"
                    }
                    className="w-full"
                    error={!!downPaymentSourceOtherError}
                    helperText={downPaymentSourceOtherError}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Total Loan Amount */}
        <div className="flex items-center justify-between h-[42px] px-6 bg-on-surface-primary rounded-full">
          <span className="text-xs font-medium text-white">
            Total Loan Amount
          </span>
          {loanAmountLoading ? (
            <Spinner variant="white" size="xs" />
          ) : (
            <div className="flex items-center text-base font-semibold text-white">
              <span>$</span>
              <span>{formatCurrency(loanAmount)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricingCalculatorSection;
